/**
 * CostCalculator — interactive slider on /pricing that lets prospects dial in
 * their own call/SMS volume and see the exact monthly Twilio + BuzzerAI total.
 * Turns our transparent pricing disclosure from a "read and trust us" moment
 * into a "you did the math yourself" moment — much higher conversion signal.
 *
 * Formulas (2026 Twilio US rates, quoted verbatim on the FAQ page):
 *   - Number rental: $1.15/mo flat
 *   - Voice (inbound to Mimi): $0.0085/min × avg call length × calls/day × 30
 *   - SMS out (table-ready alerts): $0.0079/msg × sms/day × 30
 *   - A2P 10DLC compliance: $10/mo flat
 * We use 1.5-minute avg call length (matches our real-world beta numbers).
 */
import { useState, useMemo } from "react";
import { Sliders, Info } from "@phosphor-icons/react";

const TIERS = [
  { key: "essential", name: "Essential", price: 29, blurb: "AI phone + web chat" },
  { key: "complete",  name: "Complete",  price: 49, blurb: "Everything (most restaurants pick this)" },
  { key: "multi",     name: "Multi-Loc", price: 99, blurb: "Multiple locations" },
];

// Twilio 2026 US rates — mirror the FAQ disclosure verbatim so numbers never diverge.
const TWILIO = {
  number:      1.15,   // /mo
  voicePerMin: 0.0085, // per minute inbound
  smsOutbound: 0.0079, // per message outbound
  a2p:         10.00,  // /mo compliance
  avgCallMin:  1.5,    // matches real-world beta average
};

const money = (n) => `$${n.toFixed(2)}`;

export default function CostCalculator() {
  const [calls, setCalls] = useState(12);
  const [sms, setSms] = useState(15);
  const [tier, setTier] = useState("complete");

  const totals = useMemo(() => {
    const voice = calls * TWILIO.avgCallMin * TWILIO.voicePerMin * 30;
    const smsCost = sms * TWILIO.smsOutbound * 30;
    const twilio = TWILIO.number + voice + smsCost + TWILIO.a2p;
    const plan = TIERS.find((t) => t.key === tier).price;
    return {
      twilio,
      plan,
      grand: twilio + plan,
      breakdown: { voice, smsCost },
    };
  }, [calls, sms, tier]);

  return (
    <div className="mt-8 pt-8 border-t border-[#E1DFD5]" data-testid="cost-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Sliders size={18} weight="fill" color="#D96C5B" />
        <div className="overline text-[#D96C5B]">Your own numbers</div>
      </div>
      <h3 className="font-heading text-xl md:text-2xl font-extrabold text-[#121914] mb-4 max-w-xl leading-tight">
        Not sure where you land? Drag the sliders.
      </h3>

      <div className="grid md:grid-cols-[1fr_auto] gap-8 items-start">
        {/* Inputs */}
        <div className="space-y-6">
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label htmlFor="calc-calls" className="text-sm font-semibold text-[#121914]">Calls per day</label>
              <span className="font-mono text-2xl font-extrabold text-[#1E3A2F]" data-testid="calc-calls-value">{calls}</span>
            </div>
            <input
              id="calc-calls"
              type="range"
              min={0}
              max={40}
              step={1}
              value={calls}
              onChange={(e) => setCalls(Number(e.target.value))}
              data-testid="calc-calls-slider"
              className="w-full accent-[#D96C5B] cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-[#5C6B61] mt-1 font-mono">
              <span>0</span><span>10</span><span>20</span><span>30</span><span>40+</span>
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label htmlFor="calc-sms" className="text-sm font-semibold text-[#121914]">"Table ready" texts per day</label>
              <span className="font-mono text-2xl font-extrabold text-[#1E3A2F]" data-testid="calc-sms-value">{sms}</span>
            </div>
            <input
              id="calc-sms"
              type="range"
              min={0}
              max={50}
              step={1}
              value={sms}
              onChange={(e) => setSms(Number(e.target.value))}
              data-testid="calc-sms-slider"
              className="w-full accent-[#D96C5B] cursor-pointer"
            />
            <div className="flex justify-between text-[10px] text-[#5C6B61] mt-1 font-mono">
              <span>0</span><span>10</span><span>25</span><span>40</span><span>50+</span>
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-[#121914] mb-2 block">BuzzerAI plan</label>
            <div className="grid grid-cols-3 gap-2">
              {TIERS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTier(t.key)}
                  data-testid={`calc-tier-${t.key}`}
                  className={`text-left rounded-lg border-2 p-3 transition-all ${
                    tier === t.key
                      ? "border-[#0E1512] bg-[#0E1512] text-white"
                      : "border-[#E1DFD5] bg-white text-[#121914] hover:border-[#5C6B61]"
                  }`}
                >
                  <div className="font-heading font-extrabold text-sm">{t.name}</div>
                  <div className={`text-xs mt-0.5 ${tier === t.key ? "text-white/70" : "text-[#5C6B61]"}`}>${t.price}/mo</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Output card */}
        <div className="bg-[#0E1512] text-white rounded-xl p-6 md:min-w-[280px]" data-testid="calc-output">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#F5C543] font-bold mb-3">Your monthly total</div>

          <div className="space-y-2 text-sm font-mono">
            <div className="flex justify-between text-white/80">
              <span>Twilio number</span>
              <span>{money(TWILIO.number)}</span>
            </div>
            <div className="flex justify-between text-white/80">
              <span>Voice minutes</span>
              <span data-testid="calc-voice">{money(totals.breakdown.voice)}</span>
            </div>
            <div className="flex justify-between text-white/80">
              <span>SMS delivery</span>
              <span data-testid="calc-sms-cost">{money(totals.breakdown.smsCost)}</span>
            </div>
            <div className="flex justify-between text-white/80">
              <span>A2P compliance</span>
              <span>{money(TWILIO.a2p)}</span>
            </div>
          </div>

          <div className="border-t border-white/20 mt-3 pt-3 space-y-1 text-sm font-mono">
            <div className="flex justify-between text-white/90">
              <span>Twilio subtotal</span>
              <span data-testid="calc-twilio-total">{money(totals.twilio)}</span>
            </div>
            <div className="flex justify-between text-white/90">
              <span>BuzzerAI {TIERS.find((t) => t.key === tier).name}</span>
              <span data-testid="calc-plan-total">${totals.plan.toFixed(2)}</span>
            </div>
          </div>

          <div className="border-t-2 border-[#F5C543] mt-4 pt-3 flex items-baseline justify-between">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#F5C543] font-bold">Total / mo</span>
            <span className="font-heading font-extrabold text-3xl text-[#F5C543]" data-testid="calc-grand-total">
              ${totals.grand.toFixed(0)}
            </span>
          </div>

          <div className="mt-4 flex items-start gap-1.5 text-[10px] text-white/60 leading-snug">
            <Info size={12} weight="fill" className="mt-0.5 flex-shrink-0" />
            <span>Twilio is billed by them, direct. We take zero markup. Rate: $0.0085/min voice, $0.0079/SMS.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
